import React from "react";

import CheckboxGroup from "../../../components/CheckboxGroup";
import Info from "../../../components/Info";
import PageTitle from "../../../components/PageTitle";
import Question from "../../../components/Question";
import QuestionAddress from "../../../components/QuestionAddress";
import QuestionDatepicker from "../../../components/QuestionDatepicker";
import QuestionFrame from "../../../components/QuestionsFrame";
import RadioGroup from "../../../components/RadioGroup";
import SubHeading from "../../../components/SubHeading";
import UnsafeToContactInfo from "../../../components/UnsafeToContactInfo";

import { courts } from "csv-package";
import InfoNote from "../../../components/InfoNote";
import colors from "../../../theme/colors";
import ConditionalFieldsWrapper from "../../../components/ConditionalFieldsWrapper";
import { useFormikContext } from "formik";
import { FlatApplciationType } from "../../../types";
import DynamicCourtName from "../../../components/DymamicCourtName";

const Applicant = () => {
  const { values } = useFormikContext<FlatApplciationType>();

  return (
    <>
      <PageTitle
        heading="Applicant details"
        description={
          <>
            The{" "}
            <Info
              inherit
              string="applicant|Applicant|The person who makes an application for a family violence intervention order."
            />{" "}
            is the person who is requesting to Extend, Vary or Revoke the Family
            Violence Intervention Order.
          </>
        }
      />
      <QuestionFrame>
        <Question name="appl_det_giv_nam" />
        <Question name="appl_det_fam_nam" />
        <QuestionDatepicker name="appl_det_dob" />
        <RadioGroup
          name="appl_det_sex"
          optionChildren={{
            U: <Question name="appl_det_sex_other" />,
          }}
        />
      </QuestionFrame>
      <SubHeading heading="Address details" />
      <QuestionFrame
        privateFrame
        privateHeading="Private question - ||the respondent|Respondent|The person who used family violence against you and/or your children or the protected person in this application.|| will not see this answer."
      >
        <InfoNote>
          <p style={{ marginTop: 0 }}>
            The court will keep this information <strong>secret</strong> from the
            respondent. Unless you agree to share it.
          </p>
          <p>The Court needs an address so we can send documents to you.</p>
          <p>You can provide:</p>
          <ul>
            <li>your home address</li>
            <li>your postal address (if different), or</li>
            <li>
              if you are concerned for your safety, or staying in emergency or
              temporary accommodation you can provide the address of a person
              chosen by you.
            </li>
          </ul>
        </InfoNote>
        <QuestionAddress
          label="Your current address*"
          namePrefix="appl_det_addr"
          useToDetermineCourt={values.courtId === null}
        />
        <RadioGroup
          name="appl_det_addr_secret"
          optionChildren={{
            yes: (
              <>
                <RadioGroup
                  name="appl_det_court_heard"
                  optionChildren={{
                    yes: (
                      <>
                        Your application will be sent to <DynamicCourtName />. To learn more about your local court see{" "}
                        <a
                          href="https://www.mcv.vic.gov.au/going-court/find-court"
                          target="_blank"
                          rel="noreferrer"
                          title="Find a court"
                          style={{ color: colors.blue }}
                        >
                          here
                        </a>
                        . The Court staff will discuss this application and your safety with you.
                      </>
                    ),
                    no: (
                      <>
                        Your application will be sent to the court that heard your
                        original matter. The Court staff will discuss this
                        application and your safety with you.
                      </>
                    ),
                  }}
                />
                <ConditionalFieldsWrapper
                  conditions={{
                    appl_det_court_heard: ["no"],
                  }}
                >
                  <Question
                    type="select"
                    name="CourtID"
                    options={courts
                      .filter((court) => court.enabled)
                      .sort((a, b) => (a.name > b.name ? 1 : -1))
                      .map((court) => ({
                        value: court.court_id,
                        text: court.name,
                      }))}
                  />
                </ConditionalFieldsWrapper>
              </>
            ),
            no: (
              <>
                Your application will be sent to your local court. To learn more
                about your local court see{" "}
                <a
                  href="https://www.mcv.vic.gov.au/going-court/find-court"
                  target="_blank"
                  rel="noreferrer"
                  title="Find a court"
                  style={{ color: colors.blue }}
                >
                  here
                </a>
                .
              </>
            ),
          }}
        />
      </QuestionFrame>
      <PageTitle
        heading="Court hearing"
        description="You will need to appear at court to have this application heard."
      />
      <QuestionFrame>
        <RadioGroup name="appl_det_court_in_person" />
      </QuestionFrame>
      <PageTitle
        heading="Contact details"
        description={
          <>
            <p>
              We will need to contact you about your application and what you need
              to do next. Please tell us the best and safest way to contact you.
            </p>
            <p>Please consider if your phone or email may be monitored.</p>
          </>
        }
      />
      <QuestionFrame>
        <CheckboxGroup
          name="appl_safe_cont"
          disableOthers="unsafe"
          optionChildren={{
            phone: (
              <>
                <Question name="appl_cont_phone_3" />
                <Question name="appl_cont_phone" />
                <Question name="appl_cont_phone_2" />
                <RadioGroup name="appl_cont_phone_vm" />
                <CheckboxGroup
                  name="appl_cont_safe_times"
                  disableOthers={["any"]}
                />
              </>
            ),
            email: (
              <>
                <Question name="appl_cont_email" />
              </>
            ),
            unsafe: <UnsafeToContactInfo usePostcode />,
          }}
        />
      </QuestionFrame>
    </>
  );
};

export default Applicant;
